import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { baseurl } from "../../config/url";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

/** ✅ Supports both adminToken + token (+ optional auth.token) */
const getAnyToken = () => {
  const adminToken = localStorage.getItem("adminToken");
  const token = localStorage.getItem("token");

  let authToken = null;
  try {
    const auth = JSON.parse(localStorage.getItem("auth") || "{}");
    authToken = auth?.token || null;
  } catch {}

  return adminToken || token || authToken || "";
};

const PendingOrders = () => {
  const [pendingOrders, setPendingOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const navigate = useNavigate();
  const token = useMemo(() => getAnyToken(), []);

  const closeModal = () => setSelectedOrder(null);

  // ✅ FETCH DATA
  useEffect(() => {
    let isMounted = true;

    const fetchPendingOrders = async () => {
      try {
        if (!token) {
          navigate("/admin/login");
          return;
        }

        const res = await axios.get(`${baseurl}/api/admin/orders/pending`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        });

        if (!isMounted) return;

        setPendingOrders(Array.isArray(res.data) ? res.data : []);
      } catch (error) {
        if (error?.response?.status === 401) {
          navigate("/admin/login");
        }
        if (isMounted) setPendingOrders([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchPendingOrders();

    return () => {
      isMounted = false;
    };
  }, [navigate, token]);

  // ✅ ESC CLOSE
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeModal();
    };
    if (selectedOrder) window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedOrder]);

  const openDetailsPage = (id) => {
    if (!id) return;
    closeModal();
    navigate(`/orders/${id}`);
  };

  // =========================
  // ✅ EXCEL DOWNLOAD FUNCTION
  // =========================
  const downloadExcel = () => {
    if (!pendingOrders.length) return;

    const formattedData = pendingOrders.map((order) => ({
      "Order ID": order.orderId || order._id || "N/A",
      "Status": order.latestStatus?.status || "Pending",
      "Date": order.latestStatus?.timestamp
        ? new Date(order.latestStatus.timestamp).toLocaleString("en-IN")
        : order.createdAt
        ? new Date(order.createdAt).toLocaleString("en-IN")
        : "N/A",
      "Product ID": order.latestStatus?.productId || "N/A",

      "User Name": order.userName || "N/A",
      "User Email": order.userEmail || "N/A",
      "User Mobile": order.userMobile || "N/A",

      "Seller Name": order.sellerName || "N/A",
      "Seller Email": order.sellerEmail || "N/A",
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Pending Orders");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, `Pending_Orders_${Date.now()}.xlsx`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <p className="text-lg font-semibold text-gray-600">
          Loading Pending Orders...
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      
      {/* HEADER */}
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <h1 className="text-3xl font-bold text-gray-800">
          Pending Orders
        </h1>

        {/* ✅ DOWNLOAD BUTTON */}
        <button
          onClick={downloadExcel}
          className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg shadow"
        >
          ⬇ Download Excel
        </button>
      </div>

      <div className="bg-white border rounded-lg shadow-sm overflow-x-auto">
        {pendingOrders.length > 0 ? (
          <table className="w-full text-sm">
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                <th className="px-5 py-3 text-left">Order ID</th>
                <th className="px-5 py-3 text-left">User</th>
                <th className="px-5 py-3 text-left">Seller</th>
                <th className="px-5 py-3 text-left">Date</th>
                <th className="px-5 py-3 text-left">Status</th>
              </tr>
            </thead>

            <tbody>
              {pendingOrders.map((order, index) => {
                const dateValue =
                  order.latestStatus?.timestamp || order.createdAt;

                return (
                  <tr
                    key={order._id || index}
                    className="border-t hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedOrder(order)}
                  >
                    <td className="px-5 py-4 font-medium">
                      {order.orderId || order._id}
                    </td>

                    <td className="px-5 py-4">
                      <div className="font-semibold">
                        {order.userName || "N/A"}
                      </div>
                      <div className="text-xs text-gray-500">
                        {order.userEmail}
                      </div>
                    </td>

                    <td className="px-5 py-4">
                      <div className="font-semibold">
                        {order.sellerName || "N/A"}
                      </div>
                      <div className="text-xs text-gray-500">
                        {order.sellerEmail}
                      </div>
                    </td>

                    <td className="px-5 py-4">
                      {dateValue
                        ? new Date(dateValue).toLocaleString("en-IN")
                        : "N/A"}
                    </td>

                    <td className="px-5 py-4 text-yellow-700 font-semibold">
                      {order.latestStatus?.status || "Pending"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="p-6 text-center text-gray-500">
            No pending orders found.
          </div>
        )}
      </div>

      {/* MODAL */}
      {selectedOrder && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50"
          onClick={closeModal}
        >
          <div
            className="bg-white w-full max-w-2xl rounded-lg shadow-lg border"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between px-5 py-4 border-b">
              <h2 className="text-xl font-bold">Order Details</h2>
              <button onClick={closeModal}>✕</button>
            </div>

            <div className="p-5 space-y-3 text-sm">
              <DetailRow label="Order ID" value={selectedOrder.orderId || selectedOrder._id} />
              <DetailRow label="Status" value={selectedOrder.latestStatus?.status} />
              <DetailRow label="User" value={selectedOrder.userName} />
              <DetailRow label="Seller" value={selectedOrder.sellerName} />
            </div>

            <div className="px-5 py-4 border-t flex justify-end gap-2">
              <button onClick={closeModal} className="px-4 py-2 bg-gray-200 rounded">
                Close
              </button>

              <button
                onClick={() =>
                  openDetailsPage(selectedOrder.orderId || selectedOrder._id)
                }
                className="px-4 py-2 bg-blue-600 text-white rounded"
              >
                View
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const DetailRow = ({ label, value }) => (
  <div className="flex gap-4">
    <div className="w-40 text-gray-500">{label}</div>
    <div className="font-semibold">{value || "N/A"}</div>
  </div>
);

export default PendingOrders;